/**
 * affiliateContext — maps a content page's slug/stage to the affiliate
 * "project_type" (or journey stage) the backend recommender understands.
 *
 * Supplier-agnostic by design: the backend NEEDS matrix maps project_type ->
 * product categories, and the products come from whichever Suppliers stock
 * those categories. Onboarding a new Supplier makes it appear on every page
 * that resolves to a matching type here — no page edits needed. Returns null
 * when a page has no relevant Supplier coverage, so the product strip hides.
 */

// Page slug (learn material slug, or pillar page slug in its many SA/VIC
// variants) -> canonical affiliate project_type.
const SLUG_TO_PROJECT_TYPE = {
  landscaping: 'landscaping',
  'landscaping-adelaide': 'landscaping',
  'swimming-pools': 'swimming-pools',
  'swimming-pools-adelaide': 'swimming-pools',
  'swimming-pool-builders-adelaide': 'swimming-pools',
  'swimming-pool-builders-melbourne': 'swimming-pools',
  'verandahs-pergolas': 'verandahs-pergolas',
  'verandahs-adelaide': 'verandahs-pergolas',
  'verandahs-and-pergolas-adelaide': 'verandahs-pergolas',
  'verandahs-and-pergolas-melbourne': 'verandahs-pergolas',
  'sheds-garages': 'sheds-garages',
  'sheds-adelaide': 'sheds-garages',
  decking: 'verandahs-pergolas',
  'decking-adelaide': 'verandahs-pergolas',
  'building-a-new-home': 'building-a-new-home',
  'knockdown-rebuild': 'knockdown-rebuild',
  // Renovation content -> renovations type (now surfaces myHomeware bathroom &
  // kitchen fixtures via the bathroom/kitchen-renovation NEEDS). Suburb variants
  // included so local reno pages match too.
  renovation: 'renovations',
  renovations: 'renovations',
  'renovation-melbourne': 'renovations',
  'renovate-and-extend': 'renovations',
  'renovation-extension': 'renovations',
  'heritage-renovation': 'renovations',
  'restoring-a-period-home': 'renovations',
  'bathroom-renovations-adelaide': 'renovations',
  'bathroom-renovations-melbourne': 'renovations',
  'kitchen-renovations-adelaide': 'renovations',
  'kitchen-renovations-melbourne': 'renovations',
  'setting-up-your-bedroom': 'bedroom',
  'nursery-setup': 'bedroom',
  'guest-room-setup': 'bedroom',
  'mattress-sizing-guide': 'bedroom',
  'establishing-a-new-lawn': 'landscaping',
  'setting-up-your-backyard': 'landscaping',
  'pressure-washing-guide': 'landscaping',
  'outdoor-entertaining-setup': 'verandahs-pergolas',
  'cleaning-a-new-build-before-moving-in': 'home-cleaning',
  'new-home-cleaning-checklist': 'home-cleaning',
  'managing-construction-dust': 'home-cleaning',
  'robot-vacuum-buying-guide': 'home-cleaning',
  'styling-a-new-kitchen': 'interior-styling',
  'dining-table-styling': 'interior-styling',
  'living-room-styling': 'interior-styling',
  'home-fragrance-guide': 'interior-styling',
  'louvered-pergola-buying-guide': 'verandahs-pergolas',
  'sofa-buying-guide': 'interior-styling',
  'dining-table-buying-guide': 'interior-styling',
  'rug-buying-guide': 'interior-styling',
  'home-lighting-buying-guide': 'interior-styling',
  'bed-frame-buying-guide': 'interior-styling',
  'bedroom-storage-guide': 'interior-styling',
  'office-desk-buying-guide': 'interior-styling',
  'office-chair-buying-guide': 'interior-styling',
  'cookware-buying-guide': 'interior-styling',
  'kitchen-knife-buying-guide': 'interior-styling',
  'dinnerware-buying-guide': 'interior-styling',
  'glassware-buying-guide': 'interior-styling',
  'pergola-vs-verandah': 'verandahs-pergolas',
  'enclosing-a-pergola': 'verandahs-pergolas',
  'pergola-lighting-and-heating': 'verandahs-pergolas',
};

export const affiliateProjectType = (slug) => {
  if (!slug) return null;
  const clean = String(slug).replace(/^\//, '').replace(/\/$/, '').toLowerCase();
  return SLUG_TO_PROJECT_TYPE[clean] || null;
};

// Learn-guide slug -> the indexable /products department it naturally maps to.
// Used to render a descriptive "shop the range" anchor from a buying guide into
// the commercial department page (internal linking for topical clusters).
// Only guides that correspond to physical products our partners stock appear
// here — build/contract/regulatory guides deliberately do NOT.
const GUIDE_TO_DEPARTMENT = {
  'sofa-buying-guide': 'furniture',
  'dining-table-buying-guide': 'furniture',
  'office-desk-buying-guide': 'furniture',
  'office-chair-buying-guide': 'furniture',
  'rug-buying-guide': 'lighting-decor',
  'home-lighting-buying-guide': 'lighting-decor',
  'home-fragrance-guide': 'lighting-decor',
  'living-room-styling': 'lighting-decor',
  'bed-frame-buying-guide': 'bedroom-sleep',
  'bedroom-storage-guide': 'bedroom-sleep',
  'mattress-sizing-guide': 'bedroom-sleep',
  'setting-up-your-bedroom': 'bedroom-sleep',
  'cookware-buying-guide': 'kitchen-dining',
  'kitchen-knife-buying-guide': 'kitchen-dining',
  'dinnerware-buying-guide': 'kitchen-dining',
  'glassware-buying-guide': 'kitchen-dining',
  'styling-a-new-kitchen': 'kitchen-dining',
  'setting-up-your-backyard': 'outdoor-garden',
  'outdoor-entertaining-setup': 'outdoor-garden',
  'establishing-a-new-lawn': 'outdoor-garden',
  'louvered-pergola-buying-guide': 'outdoor-garden',
  'robot-vacuum-buying-guide': 'cleaning-tools',
  'managing-construction-dust': 'cleaning-tools',
  'pressure-washing-guide': 'cleaning-tools',
};

export const productDepartmentForGuide = (slug) => {
  if (!slug) return null;
  const clean = String(slug).replace(/^\//, '').replace(/\/$/, '').toLowerCase();
  return GUIDE_TO_DEPARTMENT[clean] || null;
};

// Journey stage -> is gear genuinely relevant at this lifecycle stage? Only
// the hands-on stages resolve; early research stages return null so a browser
// in "dream/plan/choose" is never shown gear.
const GEAR_RELEVANT_STAGES = new Set(['build', 'own']);
export const affiliateLifecycleStage = (stageKey) =>
  (stageKey && GEAR_RELEVANT_STAGES.has(stageKey)) ? stageKey : null;
